import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import DataTable from "../../../components/RestaurantAdminComponents/DataTable";
import FilterToolbar from "../../../components/Common/FilterToolbar";
import { Container } from "reactstrap";
import { get } from "../../../api/axios";
import endpoints from "../../../api/endpoints";
import toastr from "toastr";
import "toastr/build/toastr.min.css";
import { extractErrorMessage } from "../../../utils/errorHandler";

/* -------------------------------------------------------------------------
 * AdminPointsLedgerPage — full "Points Ledger" table view. No sidebar or
 * header here; this is content-only, dropped into whatever shell layout
 * the app already provides.
 * ---------------------------------------------------------------------- */

const columns = [
  { key: "transaction_id", label: "Transaction ID", width: "2fr" },
  { key: "type", label: "Type", width: "1fr" },
  { key: "restaurant_name", label: "Restaurant", width: "1.5fr" },
  { key: "member_name", label: "Member Name", width: "1.2fr" },
  { key: "member", label: "Email", width: "1.5fr" },
  { key: "amount", label: "Amount", width: "1fr", render: (r) => `$${r.amount}` },
  { key: "points", label: "Points", width: "0.8fr", render: (r) => `${r.points}` },
  { key: "status", label: "Status", width: "0.8fr" },
  { key: "date_time", label: "Date & Time", width: "1.4fr" },
];

const formatLedgerType = (value) => {
  if (!value) return "Unknown";

  return String(value)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
};

const AdminPointsLedgerPage = ({ onFilterClick, onExportClick }) => {
  document.title = "Points Ledger | Omaha Food Lovers";
  const [allLedger, setAllLedger] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDirection, setSortDirection] = useState("desc");

  // Filter states
  const [type, setType] = useState("all");
  const [status, setStatus] = useState("all");
  const [vendorListingId, setVendorListingId] = useState("");
  const [memberId, setMemberId] = useState("");
  const [dateRange, setDateRange] = useState({ preset: "all", start: "", end: "" });

  // Lists for selectors
  const [restaurants, setRestaurants] = useState([]);
  const [members, setMembers] = useState([]);

  toastr.options = {
    positionClass: "toast-bottom-left",
    timeOut: 5000,
    closeButton: true,
    progressBar: true,
  };

  const buildQueryParams = (pageNum = 1) => {
    const params = new URLSearchParams();
    params.append("page", pageNum);
    params.append("per_page", 20);

    if (search && search.trim()) {
      params.append("search", search.trim());
    }

    if (type && type !== "all") {
      params.append("type", type);
    }
    if (status && status !== "all") {
      params.append("status", status);
    }
    if (vendorListingId) {
      const vendorId = typeof vendorListingId === 'string' ? vendorListingId.trim() : vendorListingId;
      if (vendorId) {
        params.append("vendor_listing_id", vendorId);
      }
    }
    if (memberId) {
      const memId = typeof memberId === 'string' ? memberId.trim() : memberId;
      if (memId) {
        params.append("member_id", memId);
      }
    }
    if (dateRange.start) {
      params.append("date_from", dateRange.start);
    }
    if (dateRange.end) {
      params.append("date_to", dateRange.end);
    }
    if (sortBy) {
      params.append("sort_by", sortBy);
    }
    if (sortDirection) {
      params.append("sort_direction", sortDirection);
    }

    const queryString = params.toString();
    return queryString;
  };

  // Fetch restaurants
  const fetchRestaurants = async () => {
    try {
      const response = await get(`${endpoints.adminAllRestaurants}&page=1&per_page=100`);
      if (response.data?.data) {
        const data = response.data.data;
        const rawRestaurants = data.data || [];

        const restaurantOptions = rawRestaurants.map((r) => ({
          value: r.id,
          label: r.title || r.name || `Restaurant ${r.id}`,
        }));

        setRestaurants(restaurantOptions);
      }
    } catch (error) {
      console.error("Failed to fetch restaurants:", error);
    }
  };

  // Fetch members
  const fetchMembers = async () => {
    try {
      const response = await get(`${endpoints.adminAllUsers}?page=1&per_page=100&sort_by=created_at&sort_direction=desc&member_type=customer`);
      if (response.data?.data) {
        const data = response.data.data;
        const rawMembers = data.data || [];

        const memberOptions = rawMembers.map((m) => ({
          value: m.id,
          label: `${m.full_name || m.name || 'Unknown'} (${m.email || 'No email'})`,
        }));

        setMembers(memberOptions);
      }
    } catch (error) {
      console.error("Failed to fetch members:", error);
    }
  };

  const transformLedgerData = (ledgerArray) =>
    ledgerArray.map((item) => ({
      id: item.id,
      transaction_id: item.transaction_id || 'N/A',
      date_time: item.date_time || 'Unknown',
      type: formatLedgerType(
        item.ledger_type
        || item.raw_type
        || item.type_key
        || item.type
      ),
      restaurant_name: item.restaurant?.name || item.restaurant?.title || item.restaurant_name || 'N/A',
      member_name: typeof item.member_name === 'object' && item.member_name !== null
        ? (item.member_name.name || `${item.member_name.first_name || ''} ${item.member_name.last_name || ''}`.trim() || 'Unknown')
        : (item.member_name || 'Unknown'),
      member: typeof item.member === 'object' && item.member !== null
        ? (item.member.email || item.member.name || 'N/A')
        : (item.member || 'N/A'),
      amount: item.amount || item.transaction_amount || 'N/A',
      points: item.points || 0,
      status: item.status || 'Unknown',
    }));

  const fetchLedger = async (pageNum = 1, isLoadMore = false) => {
    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      const queryParams = buildQueryParams(pageNum);
      const fullUrl = `${endpoints.adminPointsLedger}?${queryParams}`;
      
      const response = await get(fullUrl);

      if (response.data?.status || response.data?.success) {
        const data = response.data.data;
        const newLedger = data.data || [];
        const transformedLedger = transformLedgerData(newLedger);

        if (isLoadMore) {
          setAllLedger((prev) => [...prev, ...transformedLedger]);
        } else {
          setAllLedger(transformedLedger);
        }

        const currentPage = data.current_page || 1;
        const lastPage = data.last_page || 1;
        setHasMore(currentPage < lastPage);
        setPage(currentPage);
      }
    } catch (error) {
      console.error("Failed to fetch ledger:", error);
      console.error("Error details:", error.response);
      toastr.error("Failed to fetch ledger data");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      fetchLedger(page + 1, true);
    }
  };

  const handleSearchChange = (value) => {
    setSearch(value);
    setPage(1);
    setHasMore(true);
  };

  const handleExport = async () => {
    try {
      const response = await get(`${endpoints.adminExport}?type=points_ledger`);
      
      // Create a blob from the response data
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'points_ledger.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toastr.success("Export successful!");
    } catch (error) {
      console.error("Failed to export data:", error);
      const errorMessage = extractErrorMessage(error);
      toastr.error(errorMessage);
    }
  };

  // Filter configuration for FilterToolbar
  const filters = [
    {
      key: "dateRange",
      type: "dateRange",
      label: "Date Range",
      value: dateRange,
      onChange: setDateRange,
      defaultValue: { preset: "all", start: "", end: "" },
    },
    {
      key: "type",
      type: "select",
      label: "Type",
      value: type === "all" ? "" : type,
      onChange: (val) => {
        setType(val || "all");
        setPage(1);
        setHasMore(true);
      },
      options: [
        { value: "credit", label: "Credit" },
        { value: "debit", label: "Debit" },
        { value: "manual_adjustment", label: "Manual Adjustment" },
        { value: "voucher_redemption", label: "Voucher Redemption" },
        { value: "referral_referrer", label: "Referrer" },
        { value: "game", label: "Game" },
      ],
    },
    {
      key: "status",
      type: "status",
      label: "Status",
      value: status === "all" ? [] : [status],
      onChange: (val) => setStatus(val.length === 0 ? "all" : val[0]),
      options: [
        { value: "credit", label: "Credit", tone: "green" },
        { value: "debit", label: "Debit", tone: "red" },
      ],
    },
    {
      key: "restaurant",
      type: "select",
      label: "Restaurant",
      value: vendorListingId,
      onChange: setVendorListingId,
      options: restaurants,
      searchable: true,
    },
    {
      key: "member",
      type: "select",
      label: "Member",
      value: memberId,
      onChange: setMemberId,
      options: members,
      searchable: true,
    },
    {
      key: "sortBy",
      type: "sortBy",
      label: "Sort By",
      value: sortBy,
      onChange: setSortBy,
      defaultValue: "created_at",
      options: [
        { value: "created_at", label: "Created At" },
        { value: "points", label: "Points" },
        { value: "type", label: "Type" },
        { value: "status", label: "Status" },
        { value: "user_name", label: "User Name" },
        { value: "member_name", label: "Member Name" },
        { value: "email", label: "Email" },
        { value: "source_type", label: "Source Type" },
        { value: "restaurant_name", label: "Restaurant Name" },
        { value: "transaction_amount", label: "Amount" },
        { value: "transaction_reference", label: "Transaction Reference" },
      ],
    },
    {
      key: "sortDirection",
      type: "sortDirection",
      label: "Sort Direction",
      value: sortDirection,
      onChange: setSortDirection,
      defaultValue: "desc",
    },
  ];

  useEffect(() => {
    fetchRestaurants();
    fetchMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchLedger(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, sortBy, sortDirection, type, status, vendorListingId, memberId, dateRange]);

  return (
    <div className="page-content rp-page-content">
      <Container fluid>
        <div className="rp-page">
          <div>
            <h2 className="rp-card__title" style={{ margin: 0 }}>Points Ledger</h2>
            <p className="rp-card__subtitle" style={{ margin: 0 }}>Overview of points transactions and history.</p>
          </div>
        <FilterToolbar
          searchValue={search}
          onSearchChange={handleSearchChange}
          searchPlaceholder="Search by email or name"
          filters={filters}
          showExport
          onExport={handleExport}
        />

        <DataTable
          columns={columns}
          rows={allLedger}
          getRowKey={(r) => r.id}
          loading={loading}
          loadingMore={loadingMore}
          onLoadMore={handleLoadMore}
          hasMore={hasMore}
        /></div>
      </Container>
    </div>
  );
};

AdminPointsLedgerPage.propTypes = {
  onFilterClick: PropTypes.func,
  onExportClick: PropTypes.func,
};

AdminPointsLedgerPage.defaultProps = {
  onFilterClick: () => {},
  onExportClick: () => {},
};

export default AdminPointsLedgerPage;
